/* @ts-self-types="./rts_sim.d.ts" */

/**
 * Handle passed across the wasm boundary.
 */
export class Sim {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SimFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_sim_free(ptr, 0);
    }
    /**
     * Refreshes the render buffers. Call once per rendered frame, then read
     * the pointers below.
     */
    build_view() {
        wasm.sim_build_view(this.__wbg_ptr);
    }
    /**
     * @param {number} player
     * @param {number} kind
     * @param {number} tx
     * @param {number} ty
     * @returns {boolean}
     */
    can_place(player, kind, tx, ty) {
        const ret = wasm.sim_can_place(this.__wbg_ptr, player, kind, tx, ty);
        return ret !== 0;
    }
    /**
     * State hash for desync detection. Clients compare this periodically.
     * @returns {bigint}
     */
    checksum() {
        const ret = wasm.sim_checksum(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @param {number} player
     * @returns {number}
     */
    credits(player) {
        const ret = wasm.sim_credits(this.__wbg_ptr, player);
        return ret;
    }
    /**
     * @returns {number}
     */
    entity_count() {
        const ret = wasm.sim_entity_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {number} id
     * @returns {number}
     */
    entity_kind(id) {
        const ret = wasm.sim_entity_kind(this.__wbg_ptr, id);
        return ret;
    }
    /**
     * @param {number} id
     * @returns {number}
     */
    entity_owner(id) {
        const ret = wasm.sim_entity_owner(this.__wbg_ptr, id);
        return ret;
    }
    /**
     * @returns {number}
     */
    entity_ptr() {
        const ret = wasm.sim_entity_ptr(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    event_count() {
        const ret = wasm.sim_event_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    event_ptr() {
        const ret = wasm.sim_event_ptr(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {boolean}
     */
    game_over() {
        const ret = wasm.sim_game_over(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Whether an entity is alive, so the client can prune stale selections.
     * @param {number} id
     * @returns {boolean}
     */
    is_alive(id) {
        const ret = wasm.sim_is_alive(this.__wbg_ptr, id);
        return ret !== 0;
    }
    /**
     * @param {number} player
     * @returns {boolean}
     */
    is_defeated(player) {
        const ret = wasm.sim_is_defeated(this.__wbg_ptr, player);
        return ret !== 0;
    }
    /**
     * @returns {number}
     */
    map_height() {
        const ret = wasm.sim_map_height(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    map_width() {
        const ret = wasm.sim_map_width(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {number} width
     * @param {number} height
     * @param {number} players
     * @param {number} seed_lo
     * @param {number} seed_hi
     */
    constructor(width, height, players, seed_lo, seed_hi) {
        const ret = wasm.sim_new(width, height, players, seed_lo, seed_hi);
        this.__wbg_ptr = ret;
        SimFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Kind id of a structure waiting to be placed, or 255 if none.
     * @param {number} player
     * @returns {number}
     */
    pending_structure(player) {
        const ret = wasm.sim_pending_structure(this.__wbg_ptr, player);
        return ret;
    }
    /**
     * Entity at a world position, or `u32::MAX` if the point hits nothing.
     * Used for click-to-select and click-to-attack.
     * @param {number} x
     * @param {number} y
     * @returns {number}
     */
    pick(x, y) {
        const ret = wasm.sim_pick(this.__wbg_ptr, x, y);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    projectile_count() {
        const ret = wasm.sim_projectile_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    projectile_ptr() {
        const ret = wasm.sim_projectile_ptr(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Production queue contents for one category, as kind ids.
     * @param {number} player
     * @param {number} queue
     * @returns {Uint8Array}
     */
    queue_items(player, queue) {
        const ret = wasm.sim_queue_items(this.__wbg_ptr, player, queue);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Progress of the item at the front of a queue, 0..=100.
     * @param {number} player
     * @param {number} queue
     * @returns {number}
     */
    queue_progress(player, queue) {
        const ret = wasm.sim_queue_progress(this.__wbg_ptr, player, queue);
        return ret >>> 0;
    }
    /**
     * Ids of a player's units inside a world-space rectangle. Buildings are
     * excluded so a drag over your base selects the army, not the base.
     * @param {number} owner
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @returns {Uint32Array}
     */
    select_in_rect(owner, x0, y0, x1, y1) {
        const ret = wasm.sim_select_in_rect(this.__wbg_ptr, owner, x0, y0, x1, y1);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Local-player selection, used by the renderer to draw highlights. The
     * selection itself lives in JS because it is not part of game state.
     * @param {Uint32Array} ids
     */
    set_selection(ids) {
        const ptr0 = passArray32ToWasm0(ids, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.sim_set_selection(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Advances exactly one simulation tick, applying the packed command buffer
     * for that tick. Passing the same bytes on every client is what keeps the
     * simulation in lockstep.
     * @param {Uint8Array} commands
     */
    step(commands) {
        const ptr0 = passArray8ToWasm0(commands, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.sim_step(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * @returns {number}
     */
    tick() {
        const ret = wasm.sim_tick(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Terrain changes as ore is mined, so the renderer re-reads this when the
     * dirty flag says something moved.
     * @param {number} x
     * @param {number} y
     * @returns {number}
     */
    tile_at(x, y) {
        const ret = wasm.sim_tile_at(this.__wbg_ptr, x, y);
        return ret;
    }
    /**
     * Raw terrain tile ids, one byte per tile, row-major.
     * @returns {Uint8Array}
     */
    tiles() {
        const ret = wasm.sim_tiles(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {number}
     */
    winner() {
        const ret = wasm.sim_winner(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) Sim.prototype[Symbol.dispose] = Sim.prototype.free;

/**
 * Layout constants, so the TypeScript side never hardcodes a stride that
 * could drift out of sync with the Rust struct.
 * @returns {number}
 */
export function entity_stride() {
    const ret = wasm.entity_stride();
    return ret >>> 0;
}

/**
 * @returns {number}
 */
export function event_stride() {
    const ret = wasm.event_stride();
    return ret >>> 0;
}

/**
 * @returns {number}
 */
export function projectile_stride() {
    const ret = wasm.projectile_stride();
    return ret >>> 0;
}

/**
 * @param {number} kind
 * @returns {number}
 */
export function unit_cost(kind) {
    const ret = wasm.unit_cost(kind);
    return ret;
}

/**
 * @param {number} kind
 * @returns {number}
 */
export function unit_footprint(kind) {
    const ret = wasm.unit_footprint(kind);
    return ret;
}

/**
 * @param {number} kind
 * @returns {boolean}
 */
export function unit_is_building(kind) {
    const ret = wasm.unit_is_building(kind);
    return ret !== 0;
}

/**
 * Stats lookup for the HUD, so unit costs and names live in one place.
 * @param {number} kind
 * @returns {string}
 */
export function unit_name(kind) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.unit_name(kind);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg___wbindgen_throw_ea4887a5f8f9a9db: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./rts_sim_bg.js": import0,
    };
}

const SimFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_sim_free(ptr, 1));

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('rts_sim_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
